import { setQueriesDataFromKey } from '@pba/react-query';
import type { ChatroomDataObject, MessageViewDataObject } from '#models/services';
import { useQueryClient } from '#react-query';
import { type ChatroomsQueryData, getChatroomDetailQueryKey } from '../use-get-chatroom';
import { type ChatroomsInfiniteQueryData, getChatroomsInfiniteQueryKey } from '../use-get-chatrooms';

const applyNewMessageToChatroom = ({
  addUnreadCount = false,
  chatroom,
  newMessage,
}: {
  addUnreadCount?: boolean;
  chatroom: ChatroomDataObject;
  newMessage: MessageViewDataObject;
}) => ({
  ...chatroom,
  message: newMessage,
  updatedAt: newMessage.updatedAt,
  ...(chatroom.stats &&
    addUnreadCount && {
      stats: { ...chatroom.stats, unread: chatroom.stats.unread + 1 },
    }),
});

const updateChatroomsInfiniteQueryData = ({
  addUnreadCount = false,
  chatroomId,
  data,
  newMessage,
}: {
  addUnreadCount?: boolean;
  chatroomId: string;
  data: ChatroomsInfiniteQueryData['data'];
  newMessage: MessageViewDataObject;
}) => {
  const existingChatroom = data.pages.flat().find((chatroom) => chatroom?._id === chatroomId);

  if (!existingChatroom) {
    return null;
  }

  const updatedChatroom = applyNewMessageToChatroom({ addUnreadCount, chatroom: existingChatroom, newMessage });

  const pages = data.pages.map((page) => page.map((chatroom) => (chatroom?._id === chatroomId ? null : chatroom)));

  return {
    ...data,
    pages: [[updatedChatroom, ...(pages[0] ?? [])], ...pages.slice(1)],
  };
};

export const useUpdateChatroomsCache = () => {
  const queryClient = useQueryClient();

  return ({
    addUnreadCount = false,
    chatroomId,
    newMessage,
  }: {
    addUnreadCount?: boolean;
    chatroomId: string;
    newMessage: MessageViewDataObject;
  }) => {
    setQueriesDataFromKey<ChatroomsQueryData>(
      queryClient,
      getChatroomDetailQueryKey({ id: chatroomId }),
      (detailChatroom) =>
        detailChatroom
          ? applyNewMessageToChatroom({ addUnreadCount, chatroom: detailChatroom, newMessage })
          : detailChatroom,
    );

    const chatroomsInfiniteQueries = queryClient.getQueryCache().findAll({
      queryKey: getChatroomsInfiniteQueryKey({}),
    });

    for (const query of chatroomsInfiniteQueries) {
      const data = queryClient.getQueryData<ChatroomsInfiniteQueryData['data']>(query.queryKey);

      if (!data) {
        continue;
      }

      const updatedData = updateChatroomsInfiniteQueryData({ addUnreadCount, chatroomId, data, newMessage });

      if (!updatedData) {
        continue;
      }

      queryClient.setQueryData<ChatroomsInfiniteQueryData['data']>(query.queryKey, updatedData);
    }
  };
};
